import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import styles from './TemplatesPage.module.css';

interface Template {
  id: number;
  name: string;
  description: string;
  prompt_template: string;
  variables: Array<{
    name: string;
    description: string;
    required: boolean;
    default_value?: string;
  }>;
  output_format: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

export function TemplatesPage() {
  const { isAdmin } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt_template: '',
    output_format: 'markdown',
    is_default: false,
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<Template[]>('/templates?include_inactive=true');
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (err) {
      setError('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (template: Template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      prompt_template: template.prompt_template,
      output_format: template.output_format,
      is_default: template.is_default,
    });
    setIsEditing(false);
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      prompt_template: '',
      output_format: 'markdown',
      is_default: false,
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setError(null);

    try {
      if (selectedTemplate) {
        const response = await api.put(`/templates/${selectedTemplate.id}`, formData);
        if (!response.success) {
          setError(response.error || 'Failed to update template');
          return;
        }
      } else {
        const response = await api.post('/templates', formData);
        if (!response.success) {
          setError(response.error || 'Failed to create template');
          return;
        }
      }

      await loadTemplates();
      setIsEditing(false);
    } catch (err) {
      setError('Failed to save template');
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await api.delete(`/templates/${selectedTemplate.id}`);
      if (response.success) {
        setSelectedTemplate(null);
        await loadTemplates();
      } else {
        setError(response.error || 'Failed to delete template');
      }
    } catch (err) {
      setError('Failed to delete template');
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await api.post(`/templates/${selectedTemplate.id}/set-default`);
      if (response.success) {
        await loadTemplates();
        setSelectedTemplate((prev) => (prev ? { ...prev, is_default: true } : null));
      } else {
        setError(response.error || 'Failed to set default template');
      }
    } catch (err) {
      setError('Failed to set default template');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Templates</h1>
          <p className={styles.subtitle}>Manage commentary generation templates</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={handleCreate}>
            New Template
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.content}>
        {/* Template List */}
        <div className={styles.sidebar}>
          {isLoading ? (
            <div className={styles.loading}>
              <div className="spinner" />
            </div>
          ) : templates.length === 0 ? (
            <p className={styles.emptyList}>No templates found</p>
          ) : (
            <div className={styles.list}>
              {templates.map((t) => (
                <button
                  key={t.id}
                  className={`${styles.listItem} ${
                    selectedTemplate?.id === t.id ? styles.listItemActive : ''
                  } ${!t.is_active ? styles.listItemInactive : ''}`}
                  onClick={() => handleSelect(t)}
                >
                  <div className={styles.listItemTitle}>
                    {t.name}
                    {t.is_default && <span className="badge badge-primary">Default</span>}
                  </div>
                  <div className={styles.listItemDescription}>{t.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Template Detail/Editor */}
        <div className={styles.main}>
          {isEditing ? (
            <div className={styles.editor}>
              <h2>{selectedTemplate ? 'Edit Template' : 'New Template'}</h2>

              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Template name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description"
                />
              </div>

              <div className="form-group">
                <label className="form-label">System Prompt</label>
                <textarea
                  className="form-textarea"
                  value={formData.prompt_template}
                  onChange={(e) =>
                    setFormData({ ...formData, prompt_template: e.target.value })
                  }
                  placeholder="Enter the system prompt for commentary generation..."
                  rows={12}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Output Format</label>
                <select
                  className="form-select"
                  value={formData.output_format}
                  onChange={(e) =>
                    setFormData({ ...formData, output_format: e.target.value })
                  }
                >
                  <option value="markdown">Markdown</option>
                  <option value="html">HTML</option>
                  <option value="plain">Plain Text</option>
                </select>
              </div>

              <div className={styles.editorActions}>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save Template
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    setIsEditing(false);
                    if (!selectedTemplate) setSelectedTemplate(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : selectedTemplate ? (
            <div className={styles.detail}>
              <div className={styles.detailHeader}>
                <h2>{selectedTemplate.name}</h2>
                {selectedTemplate.is_default && (
                  <span className="badge badge-primary">Default Template</span>
                )}
              </div>

              {selectedTemplate.description && (
                <p className={styles.detailDescription}>{selectedTemplate.description}</p>
              )}

              <div className={styles.detailSection}>
                <h3>System Prompt</h3>
                <pre className={styles.promptPreview}>{selectedTemplate.prompt_template}</pre>
              </div>

              <div className={styles.detailMeta}>
                <span>Format: {selectedTemplate.output_format}</span>
                <span>Status: {selectedTemplate.is_active ? 'Active' : 'Inactive'}</span>
              </div>

              {isAdmin && (
                <div className={styles.actions}>
                  <button className="btn btn-outline" onClick={() => setIsEditing(true)}>
                    Edit
                  </button>
                  {!selectedTemplate.is_default && (
                    <button className="btn btn-secondary" onClick={handleSetDefault}>
                      Set as Default
                    </button>
                  )}
                  <button className="btn btn-danger" onClick={handleDelete}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.noSelection}>
              <p>Select a template to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
